import classNames from 'classnames';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCommentQuote,
  faEllipsis,
  faImage,
  faLink,
  faLock,
  faMemo,
  faPlay,
  faQuoteLeft,
  faShareFromSquare,
  faText,
  faThumbTack,
} from '@fortawesome/pro-solid-svg-icons';
import { faYoutube } from '@fortawesome/free-brands-svg-icons';
import { At, Drift, Float, Pulse } from 'components/elements/Animation';
import { HeroBadge, HeroStarfield } from 'modules/Landing';
import imgLanaDelRey from 'assets/images/lana-del-rey.png';

const BrainGlyph = () => (
  <svg viewBox="0 0 24 24" className={styles.badgeGlyph} aria-hidden="true">
    <path d="M9 4a3 3 0 0 0-3 3 3 3 0 0 0-1 5.8V15a3 3 0 0 0 4 2.8M9 4a2.5 2.5 0 0 1 3 2.4v11A2.5 2.5 0 0 1 9 19.8M15 4a3 3 0 0 1 3 3 3 3 0 0 1 1 5.8V15a3 3 0 0 1-4 2.8M15 4a2.5 2.5 0 0 0-3 2.4" />
  </svg>
);

// ── Floating memory cards ───────────────────────────────────────────────────
// Restyled from the original light cards into dark "glass" chrome so they read
// as memories drifting through the starfield rather than pasted-on white boxes.

// Glowing brand dot used as a sparse accent between cards.
const Dot = () => <div className={styles.dot} />;

// A small status pill (e.g. "Quote saved").
const Chip = ({
  icon,
  label,
  accentClass,
}: {
  icon: React.ReactNode;
  label: string;
  /** Icon tint per theme: readable mid-tone on white, pastel on navy. */
  accentClass: string;
}) => (
  <div className={styles.chip}>
    <span className={classNames(styles.chipIcon, accentClass)}>
      <svg viewBox="0 0 24 24" className={styles.chipGlyph}>
        {icon}
      </svg>
    </span>
    {label}
  </div>
);

// A Memory Box tile.
const Box = ({
  icon,
  title,
  status,
  isActive,
}: {
  icon: React.ComponentProps<typeof FontAwesomeIcon>['icon'];
  title: string;
  status: string;
  isActive: boolean;
}) => (
  <div
    className={classNames(
      styles.card,
      'flex w-56 items-center gap-3.5 px-4 py-3',
      isActive && 'ring-1 ring-mf-blue/40',
    )}
  >
    <div
      className={classNames(
        'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
        isActive
          ? 'bg-mf-blue text-white'
          : 'bg-[#F4F4F4] text-mf-gray-500 marketing-dark:bg-white/10 marketing-dark:text-white/70',
      )}
    >
      <FontAwesomeIcon icon={icon} className="text-lg" />
    </div>
    <div className="flex flex-col">
      <span className="text-[15px] font-semibold text-mf-off-black-600 marketing-dark:text-white">
        {title}
      </span>
      <span className="text-xs text-mf-gray-500 marketing-dark:text-white/45">
        {status}
      </span>
    </div>
  </div>
);

// A collection chip (emoji + name).
const Collection = ({ emoji, title }: { emoji: string; title: string }) => (
  <div
    className={classNames(
      styles.card,
      'flex w-56 items-center gap-3 px-4 py-3',
    )}
  >
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F4F4F4] marketing-dark:bg-white/10">
      <span className="text-2xl">{emoji}</span>
    </div>
    <span className="text-[15px] font-medium text-mf-off-black-600 marketing-dark:text-white">
      {title}
    </span>
  </div>
);

const CardFooter = ({ time }: { time: string }) => (
  <div className="flex select-none items-center justify-between text-xs text-mf-gray-400 marketing-dark:text-white/40">
    <span>{time}</span>
    <div className="flex items-center gap-3">
      <FontAwesomeIcon icon={faShareFromSquare} />
      <FontAwesomeIcon icon={faEllipsis} />
    </div>
  </div>
);

// A saved quote.
const Quote = ({
  content,
  author,
  time = '1d',
}: {
  content: string;
  author: string;
  time?: string;
}) => (
  <div className={classNames(styles.card, 'flex w-64 flex-col gap-3 p-4')}>
    <div>
      <FontAwesomeIcon
        icon={faQuoteLeft}
        className="mb-1.5 text-lg text-mf-blue marketing-dark:text-mf-coral"
      />
      <p className="text-[13px] italic leading-relaxed text-mf-off-black-500 marketing-dark:text-white/85">
        {content}
      </p>
    </div>
    <p className="text-[13px] font-medium text-mf-off-black-600 marketing-dark:text-white">
      <span className="mr-2 select-none text-mf-gray-400 marketing-dark:text-white/40">
        —
      </span>
      {author}
    </p>
    <CardFooter time={time} />
  </div>
);

// A saved video.
const Video = ({ title, time = '1d' }: { title: string; time?: string }) => (
  <div
    className={classNames(styles.card, 'flex w-64 flex-col overflow-hidden')}
  >
    <div className="relative">
      <Image
        src={imgLanaDelRey}
        alt={title}
        className="h-28 w-full object-cover"
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black/40 backdrop-blur-sm">
          <FontAwesomeIcon
            icon={faPlay}
            className="pl-0.5 text-xs text-white"
          />
        </div>
      </div>
    </div>
    <div className="flex flex-col gap-1.5 p-3.5">
      <FontAwesomeIcon
        icon={faYoutube}
        className="w-fit text-lg text-red-500"
      />
      <span className="text-[13px] font-bold leading-snug text-mf-off-black-600 marketing-dark:text-white">
        {title}
      </span>
      <p className="text-xs text-mf-gray-400 marketing-dark:text-white/40">
        youtube.com
      </p>
      <CardFooter time={time} />
    </div>
  </div>
);

// A pinned text note (e.g. the E=mc² memory).
const Text = ({
  title,
  content,
  sourceLabel,
  source,
  time,
}: {
  title: string;
  content: string;
  sourceLabel: string;
  source: string;
  time: string;
}) => (
  <div className={classNames(styles.card, 'flex w-64 flex-col gap-3 p-4')}>
    <div className="flex items-start justify-between gap-3">
      <span className="text-[15px] font-bold text-mf-off-black-600 marketing-dark:text-white">
        {title}
      </span>
      <FontAwesomeIcon
        icon={faThumbTack}
        className="shrink-0 text-sm text-mf-gray-400 marketing-dark:text-white/35"
      />
    </div>
    <p className="text-[13px] leading-relaxed text-mf-off-black-500 marketing-dark:text-white/85">
      {content}
    </p>
    <p className="text-xs text-mf-gray-400 marketing-dark:text-white/40">
      {sourceLabel} {source}
    </p>
    <CardFooter time={time} />
  </div>
);

// A circular memory-type badge (image / text / link / …) drifting in the
// starfield — the dark-glass take on the original white icon circles.
const Badge = ({
  icon,
}: {
  icon: React.ComponentProps<typeof FontAwesomeIcon>['icon'];
}) => (
  <div className={styles.badge}>
    <FontAwesomeIcon icon={icon} aria-hidden="true" />
  </div>
);

// Login brand panel — navy marketing column shown beside the auth form, with
// memories drifting through the starfield around the value proposition.
export const ShowcasePanel = ({
  variant = 'page',
  className,
}: {
  /** `modal` shrinks the floating cards to fit the smaller login dialog. */
  variant?: 'page' | 'modal';
  className?: string;
}) => {
  const t = useTranslations();
  const s = (key: string) => t(`auth.showcase.${key}`);
  return (
    <div className={classNames(styles.container, className)}>
      {/* Starfield paints the dark panel; kept mounted (visibility, not
          display) so its canvas keeps a real size across theme flips. */}
      <HeroStarfield className="invisible marketing-dark:visible" />

      {/* Light-theme glow behind the centred badge. */}
      <div className={styles.lightGlow} aria-hidden="true" />

      {/* Floating memories — scattered in a loose ring around the centred badge
          so they fill the panel evenly rather than bunching in the corners. */}
      <div
        className={classNames(
          styles.memories,
          variant === 'modal' && styles.memoriesModal,
        )}
      >
        {/* Upper arc */}
        <At top="17%" left="5%">
          <Drift duration={8.5} delay={0.4}>
            <Box
              icon={faLock}
              title={s('boxTitle')}
              status={s('statusPrivate')}
              isActive={false}
            />
          </Drift>
        </At>
        <At top="9%" left="46%">
          <Drift duration={9} delay={1.2}>
            <Collection emoji="🏺" title={s('collectionHistory')} />
          </Drift>
        </At>
        <At top="30%" left="62%">
          <Float duration={5} delay={0.2}>
            <Chip
              accentClass="bg-[rgba(247,203,45,0.16)] text-[#B18E1E] marketing-dark:bg-[rgba(253,228,122,0.13)] marketing-dark:text-[#FDE47A]"
              label={t('auth.showcase.chipQuote')}
              icon={<path d="M7 7h4v4a4 4 0 0 1-4 4M13 7h4v4a4 4 0 0 1-4 4" />}
            />
          </Float>
        </At>

        {/* Lower arc */}
        <At top="60%" left="4%">
          <Drift duration={8} delay={1}>
            <Quote
              content={s('quoteContent')}
              author="Winston Churchill"
              time={s('time1d')}
            />
          </Drift>
        </At>
        <At top="56%" left="54%">
          <Drift duration={9} delay={0.6}>
            <Video
              title="Lana Del Rey — Young and Beautiful"
              time={s('time1d')}
            />
          </Drift>
        </At>
        <At top="85%" left="26%">
          <Float duration={5.6} delay={1} y={[10, 0]}>
            <Chip
              accentClass="bg-[rgba(110,215,166,0.18)] text-[#2BA471] marketing-dark:bg-[rgba(110,215,166,0.13)] marketing-dark:text-[#6ed7a6]"
              label={t('auth.showcase.chipSaved')}
              icon={<path d="m5 12 5 5L20 7" />}
            />
          </Float>
        </At>

        {/* E=mc² text memory */}
        <At top="31%" left="8%">
          <Drift duration={8.5} delay={0.9}>
            <Text
              title="E=mc²"
              content={s('textContent')}
              sourceLabel={s('sourcePrefix')}
              source="calculatorsoup"
              time={s('time1d')}
            />
          </Drift>
        </At>

        {/* Memory-type badges drifting through the gaps */}
        <At top="6%" left="72%">
          <Float duration={4.8} delay={0.3}>
            <Badge icon={faMemo} />
          </Float>
        </At>
        <At top="30%" left="47%">
          <Float duration={4.5} delay={0} y={[10, 0]}>
            <Badge icon={faImage} />
          </Float>
        </At>
        <At top="48%" left="84%">
          <Float duration={5.2} delay={1.2}>
            <Badge icon={faText} />
          </Float>
        </At>
        <At top="64%" left="42%">
          <Float duration={4.3} delay={1.5} y={[10, 0]}>
            <Badge icon={faLink} />
          </Float>
        </At>
        <At top="83%" left="8%">
          <Float duration={5} delay={0.6}>
            <Badge icon={faCommentQuote} />
          </Float>
        </At>

        {/* Sparse accents filling the gaps around the badge */}
        <At top="22%" left="33%">
          <Pulse duration={3.5} delay={0.8}>
            <Dot />
          </Pulse>
        </At>
        <At top="44%" left="20%">
          <Pulse duration={4} delay={0.3}>
            <Dot />
          </Pulse>
        </At>
        <At top="46%" left="74%">
          <Pulse duration={3.8} delay={1.6}>
            <Dot />
          </Pulse>
        </At>
        <At top="72%" left="68%">
          <Pulse duration={3.5} delay={1}>
            <Dot />
          </Pulse>
        </At>
      </div>

      <div className={styles.content}>
        <HeroBadge
          icon={<BrainGlyph />}
          iconClassName="bg-mf-blue text-white"
          label={t('auth.showcase.badge')}
        />
      </div>
    </div>
  );
};

const styles = {
  container:
    'relative hidden overflow-hidden marketing-dark:bg-mf-navy lg:flex lg:items-center',
  lightGlow: [
    'pointer-events-none absolute left-1/2 top-1/2 h-[620px] w-[720px] max-w-full -translate-x-1/2 -translate-y-1/2 rounded-full',
    '[background:radial-gradient(ellipse_closest-side,rgba(189,222,255,0.5),transparent)]',
    'marketing-dark:hidden',
  ].join(' '),
  // Floating cards sit above the starfield but below the centred copy; they
  // never intercept clicks meant for the panel.
  memories: 'pointer-events-none absolute inset-0 z-[1] hidden xl:block',
  // Scale each card from its anchor so the layout keeps its shape.
  memoriesModal: 'lg:block [&>*]:origin-top-left [&>*]:scale-[0.8]',
  badgeGlyph: 'h-3.5 w-3.5 fill-none stroke-current stroke-2',
  content: 'relative z-[2] mx-auto max-w-[520px] px-12 xl:px-16',
  // White cards on the light page; dark "glass" chrome on navy.
  card: [
    'rounded-2xl border',
    'border-[#F0F0F0] bg-white shadow-[0_14px_36px_rgba(20,30,60,0.07)]',
    'marketing-dark:border-white/10 marketing-dark:bg-[#0e1c3f]/85 marketing-dark:backdrop-blur-md',
    'marketing-dark:shadow-[0_18px_44px_rgba(0,0,0,0.5)]',
  ].join(' '),
  badge: [
    'flex h-14 w-14 items-center justify-center rounded-full border text-lg',
    'border-[#F0F0F0] bg-white text-mf-gray-500 shadow-[0_8px_22px_rgba(20,30,60,0.06)]',
    'marketing-dark:border-white/10 marketing-dark:bg-[#0e1c3f]/85 marketing-dark:text-white/80',
    'marketing-dark:backdrop-blur-md marketing-dark:shadow-[0_14px_36px_rgba(0,0,0,0.5)]',
  ].join(' '),
  dot: 'h-2.5 w-2.5 rounded-full bg-mf-blue/70 shadow-[0_0_10px_rgba(59,130,246,0.85)]',
  chip: [
    'flex items-center gap-2.5 whitespace-nowrap rounded-xl border px-3.5 py-2.5 text-[13px] font-semibold',
    'border-[#DEDEDE] bg-white text-mf-off-black-600 shadow-[0_16px_36px_rgba(20,30,60,0.16)]',
    'marketing-dark:border-white/10 marketing-dark:bg-[#0e1c3f] marketing-dark:text-white',
    'marketing-dark:shadow-[0_16px_36px_rgba(0,0,0,0.45)]',
  ].join(' '),
  chipIcon:
    'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[8px]',
  chipGlyph: 'h-4 w-4 fill-none stroke-current stroke-2',
};
